import { useState, useRef, useEffect } from 'react';
import { useMore } from './MoreContext';
import { SelectedTabIndicator } from '../orders/_components/order-details/TabIndicators';
import { TabKey } from './_constants/const';
import { cn } from '@/utils/tailwind';

interface TabCount {
  key: string;
  label: string;
}

interface ContentsTabProps {
  tabListWithCounts: TabCount[];
}

export const ContentsTab = ({ tabListWithCounts }: ContentsTabProps) => {
  const { currentTab, setCurrentTab } = useMore();

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabBarRef = useRef<HTMLDivElement | null>(null);
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  // 인디케이터 위치/크기 상태
  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
    // top: number;
    bottom: number;
    height: number;
    bg: string;
  } | null>(null);

  // 인디케이터 위치 계산
  useEffect(() => {
    const key = hoveredTab ?? currentTab;
    if (key && tabRefs.current[key] && tabBarRef.current) {
      const node = tabRefs.current[key]!;
      const containerRect = tabBarRef.current.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      setIndicator({
        left: nodeRect.left - containerRect.left,
        width: nodeRect.width,
        bottom: containerRect.bottom - nodeRect.bottom,
        height: nodeRect.height,
        bg: hoveredTab ? 'bg-gr-50' : 'bg-gr-50',
      });
    }
  }, [hoveredTab, currentTab, tabListWithCounts]);

  return (
    <div
      ref={tabBarRef}
      className="relative flex flex-wrap gap-x-[32px] gap-y-[10px]"
      onMouseLeave={() => setHoveredTab(null)}
    >
      <SelectedTabIndicator indicator={indicator} />
      {/* 탭 버튼들 */}
      {tabListWithCounts.map((tab, idx) => (
        <TabButton
          key={tab.key}
          tab={tab}
          idx={idx}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          tabRefs={tabRefs}
          onMouseEnter={() => setHoveredTab(tab.key)}
          onMouseLeave={() => setHoveredTab(null)}
        />
      ))}
    </div>
  );
};

interface TabButtonProps {
  tab: TabCount;
  idx: number;
  currentTab: TabKey;
  setCurrentTab: (key: TabKey) => void;
  tabRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}
export const TabButton = ({
  tab,
  idx,
  currentTab,
  setCurrentTab,
  tabRefs,
  onMouseEnter,
  onMouseLeave,
}: TabButtonProps) => {
  // 인덱스에 따라 최소 너비 클래스 결정
  const minWidthClass = idx === 0 ? '' : idx === 1 ? '' : '';

  return (
    <button
      ref={(el) => (tabRefs.current[tab.key] = el)}
      className={cn(
        'relative pb-[18px] transition-colors duration-300 Body1S16',
        minWidthClass,
        currentTab === tab.key
          ? 'border-b-2 border-gr-800 text-gr-900'
          : 'border-b-2 border-transparent text-gr-500 hover:text-gr-700',
      )}
      onClick={() => setCurrentTab(tab.key as typeof currentTab)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex flex-row items-center gap-2">
        <span className="whitespace-nowrap">{tab.label}</span>
      </div>
    </button>
  );
};
